import { cur } from '@/lib/currency';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useInView } from 'framer-motion';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores';
import { GitCompare, FileText, RefreshCw, ChevronLeft, ChevronRight, HelpCircle, Image as ImageIcon, MapPin, FileCheck2 } from 'lucide-react';
import api from '@/lib/api';
import { exportBranchSummaryToPDF } from '@/lib/pdfExport';
import { BranchBalancesPanel } from '@/components/accounting/BranchBalancesPanel';
import { DateQuickFilter } from '@/components/DateQuickFilter';
import { BranchMultiSelect } from '@/components/BranchMultiSelect';
import { StatCard, StatusBadge } from '@/components/reports/ReportUI';
import { exportToCsv, slug, handleExport } from '@/lib/exportCsv';
import { exportRows } from '@/components/accounting/ExportTrio';
import { CheckExportBtn } from '@/components/CheckExportBtn';
import { useWebSocket } from '@/hooks/useWebSocket';
import { FeatureTour, useTourReplay } from '@/components/FeatureTour';
import { copyElementAsImage } from '@/lib/copyAsImage';
import { BranchOverview, AdvancedDetails } from '@/components/branch/BranchOverview';
import { VatModeToggle } from '@/components/common/VatModeToggle';
import { useVatModeStore } from '@/stores/vatModeStore';
import { GlAccountingStrip } from '@/components/accounting/GlAccountingStrip';
import { ExpenseGroupRows, InterBranchSection, PlatformSection, Section } from '@/components/accounting/BranchSummarySections';
import { CollectionsSection, FinancialPosition, SupplierBillsSection, salesExtras, useOwnerPosition } from '@/components/accounting/BranchOwnerSections';
import { CashBankPositionTable, ProfitCheck } from '@/components/accounting/BranchCashBankSections';
import { CogsDetailDialog } from '@/components/accounting/CogsDetailDialog';
import { exportBranchSummaryToExcel } from '@/components/branch/branchSummaryExcel';
import { FreshnessTag } from '@/components/common/FreshnessTag';

const fmt = (v) => (v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtSR = (v) => `${cur()} ${fmt(v)}`;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const getMonthRange = (y, m) => ({
  start: `${y}-${String(m + 1).padStart(2, '0')}-01`,
  end: `${y}-${String(m + 1).padStart(2, '0')}-${new Date(y, m + 1, 0).getDate()}`,
});

// iter933 — compact month-over-month Total Outstanding (supplier debt) chart
// for the Company Summary. Self-contained inline-SVG bars (no chart lib).
function SupplierDebtTrend() {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    api.get('/accounting/supplier-debt-trend', { params: { months: 6 } })
      .then(r => { if (alive) setSeries(r.data?.series || []); })
      .catch(() => { if (alive) setSeries([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);
  if (loading) return null;
  const max = Math.max(1, ...series.map(s => s.outstanding || 0));
  const hasAny = series.some(s => (s.outstanding || 0) > 0);
  if (!hasAny) return null;
  return (
    <Card data-testid="supplier-debt-trend">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Supplier Debt Trend · Total Outstanding (6 mo)</span>
          <span className="text-xs font-bold text-rose-600">{fmtSR(series[series.length - 1]?.outstanding || 0)}</span>
        </div>
        <div className="flex items-end gap-2 h-24">
          {series.map((s) => {
            const h = Math.round(((s.outstanding || 0) / max) * 100);
            return (
              <div key={s.month} className="flex-1 flex flex-col items-center justify-end gap-1" data-testid={`debt-bar-${s.month}`} title={fmtSR(s.outstanding)}>
                <span className="text-[8px] text-muted-foreground tabular-nums">{(s.outstanding || 0) >= 1000 ? `${Math.round(s.outstanding / 1000)}k` : Math.round(s.outstanding || 0)}</span>
                <div className="w-full rounded-t bg-rose-500/80 hover:bg-rose-600 transition-all" style={{ height: `${Math.max(h, 2)}%` }} />
                <span className="text-xs text-muted-foreground">{s.month.slice(5)}/{s.month.slice(2, 4)}</span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}


export default function BranchSummaryPage() {
  const now = new Date();
  const authUser = useAuthStore((s) => s.user);
  const canRepair = ['admin', 'owner', 'super_admin'].includes((authUser?.role || '').toLowerCase());
  const repairSalaryHistory = async () => {
    try {
      const r = await api.post('/salary-payments/backfill-branches');
      toast.success(`Scanned ${r.data?.scanned ?? 0}, Fixed ${r.data?.updated ?? 0}`);
    } catch (e) {
      toast.error(`Repair failed (HTTP ${e.response?.status ?? 'network'}): ${e.response?.data?.detail || e.message}`);
    }
  };
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [branchIds, setBranchIds] = useState([]); // [] = all permitted branches
  const branchId = branchIds.length === 1 ? branchIds[0] : '';
  const setBranchId = (id) => setBranchIds(id ? [id] : []);
  const [mode, setMode] = useState('month');
  const [selYear, setSelYear] = useState(now.getFullYear());
  const [selMonth, setSelMonth] = useState(now.getMonth());
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    api.get('/branches').then(r => setBranches(r.data || [])).catch(() => {});
  }, []);

  // Allow deep-linking: /branch-summary?branch_id=XXX pre-selects the branch.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const qp = searchParams.get('branch_id');
    if (qp && qp !== branchId) setBranchId(qp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const branchNames = branchIds.length ? branches.filter((b) => branchIds.includes(b.id)).map((b) => b.name).join(', ') : 'All branches';
  const periodLabel = mode === 'month'
    ? `${MONTHS[selMonth]} ${selYear}`
    : `${startDate || '...'} to ${endDate || '...'}`;

  const reqSeq = useRef(0);
  const fetchData = useCallback(async () => {
    const seq = ++reqSeq.current;  // only the latest request may update the screen/export (no stale all-branch data)
    setLoading(true);
    try {
      let sd, ed;
      if (mode === 'month') { const r = getMonthRange(selYear, selMonth); sd = r.start; ed = r.end; }
      else { sd = startDate; ed = endDate; }
      const params = {};
      if (sd) params.start_date = sd;
      if (ed) params.end_date = ed;
      if (branchIds.length) params.branch_ids = branchIds.join(',');
      params.include_prev = true;
      const res = await api.get('/accounting/branch-summary', { params });
      if (seq !== reqSeq.current) return;
      setData(res.data);
      // Trend sparklines (best-effort, non-blocking): 6 months ending at this period.
      api.get('/accounting/branch-summary-trend', { params: { months: 6, end_date: params.end_date || undefined, branch_ids: params.branch_ids || undefined } })
        .then((t) => setTrend(t.data))
        .catch(() => setTrend(null));
    } catch { if (seq === reqSeq.current) toast.error('Failed to load branch summary'); }
    if (seq === reqSeq.current) setLoading(false);
  }, [mode, selYear, selMonth, startDate, endDate, branchIds]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Live Bridge: refresh instantly on `balance_updated` (cross-branch txn or settlement).
  useWebSocket((msg) => { if (msg?.type === 'balance_updated') fetchData(); });

  const prevMonth = () => { if (selMonth === 0) { setSelMonth(11); setSelYear(y => y - 1); } else setSelMonth(m => m - 1); };
  const nextMonth = () => { if (selMonth === 11) { setSelMonth(0); setSelYear(y => y + 1); } else setSelMonth(m => m + 1); };

  const expenseGroups = data?.expense_groups || [];
  const rows = data?.branches || [];
  const grand = data?.grand_total;
  const prev = data?.prev;
  const prevG = prev?.grand_total;
  const period = data?.period;
  // Sales ranking for medal badges — real branches only, zero-sales branches unranked.
  const salesRanks = useMemo(() => {
    const ranked = rows
      .filter((b) => b.branch_id !== '__unassigned__' && Number(b.sales?.total) > 0)
      .sort((a, b2) => Number(b2.sales.total) - Number(a.sales.total));
    const map = {};
    ranked.forEach((b, i) => { map[b.branch_id] = i + 1; });
    return map;
  }, [rows]);
  const { replay: replayTour, forceStart: forceTour, replayKey } = useTourReplay('branch-summary-v2');

  const tourSteps = [
    { selector: '[data-testid="branch-summary-page"]', title: '🏪 Branch Summary Report', content: 'Your comprehensive branch performance view — sales split by payment method, cost of goods, categorized expenses, and final net profit per branch.', placement: 'bottom' },
    { selector: '[data-tour="bs-filters"]', title: '📅 Pick Your Period', content: 'Use the Month picker for a quick monthly view, or switch to "Date Range" for a custom window. Filter by a single branch with the dropdown on the right.', placement: 'bottom' },
    { selector: '[data-tour="bs-kpis"]', title: '💰 Company-wide Totals', content: 'Five KPIs showing: Total Sales → Cost of Goods → Balance → Expenses → Net. These update instantly as you change the period or branch.', placement: 'bottom' },
    { selector: '[data-tour="bs-table"]', title: '🏢 Compare All Branches', content: 'Every branch side-by-side. Cash/Bank/Online/Customer breakdown, COGS, category-wise expenses (Gov Fee, Salary, Ticket, ID, Utility), and bottom-line Net with margin %.', placement: 'top' },
    { selector: '[data-tour="bs-detail"]', title: '📋 Per-Branch Details', content: 'Scroll down for individual branch cards. Each shows a full P&L. Click the Image icon to copy the card — paste straight into WhatsApp, Slack, or email.', placement: 'top' },
    { selector: '[data-testid="export-branch-summary-pdf"]', title: '📄 Full PDF Export', content: 'One click exports a clean, multi-page PDF with the comparison table AND detailed per-branch breakdowns — perfect for monthly reviews.', placement: 'left' },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-5 max-w-[1400px] mx-auto" data-testid="branch-summary-page-2">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-teal-200/50">
              <GitCompare size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">Branch Summary</h1>
              <p className="text-sm text-muted-foreground mt-0.5">{periodLabel} <FreshnessTag freshness={data?.freshness} testId="branch-summary-freshness" /></p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <VatModeToggle />
            {canRepair && (
              <Button size="sm" variant="outline" className="rounded-xl border-warning/40 text-warning h-9" onClick={repairSalaryHistory} data-testid="repair-salary-history-btn">
                <RefreshCw size={14} className="mr-1.5" />Repair Salary History
              </Button>
            )}
            <Link
              to="/reconcile-unassigned"
              title="Bulk-assign sales/expenses/payments with no branch"
              data-testid="reconcile-unassigned-link"
              className="flex items-center gap-1 px-2.5 py-2 text-xs font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded-lg hover:bg-rose-100"
            >
              <MapPin size={14} /> Reconcile Unassigned
            </Link>
            <button
              onClick={replayTour}
              title="Replay product tour"
              data-testid="tour-replay-btn"
              className="flex items-center gap-1 px-2.5 py-2 text-xs font-medium text-info bg-info/10 border border-info/30 rounded-lg hover:bg-info/20"
            >
              <HelpCircle size={14} /> Tour
            </button>
            <button
              onClick={() => data && !loading && exportBranchSummaryToPDF(data, periodLabel, { inclVat: useVatModeStore.getState().inclVat, branchNames })}
              disabled={!data || loading}
              title={loading ? 'Wait — the selected branch/period is still loading' : 'Export exactly what is on screen'}
              data-testid="export-branch-summary-pdf-2"
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-muted-foreground bg-white border border-border rounded-lg hover:bg-muted/50 disabled:opacity-50"
            >
              <FileText size={14} /> Export PDF
            </button>
            <button
              onClick={() => data && !loading && exportBranchSummaryToExcel(data, branchNames)}
              disabled={!data || loading}
              title={loading ? 'Wait — the selected branch/period is still loading' : 'Excel of exactly what is on screen'}
              data-testid="export-branch-summary-excel"
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-muted-foreground bg-white border border-border rounded-lg hover:bg-muted/50 disabled:opacity-50"
            >
              <FileText size={14} /> Excel
            </button>
            <button
              onClick={() => handleExport((data?.branches || []).filter((b) => b.balances).map((b) => {
                const bl = b.balances, rp = b.receipts_payments || {}, led = bl.ledger?.closing || {}, ib = b.inter_branch_by_mode || {};
                return { branch: b.branch_name, basis: bl.basis,
                  cash_opening: bl.cash.opening_movements, cash_period: bl.cash.period_movement, cash_closing: bl.cash.calculated_closing, cash_ledger: led.cash ?? '',
                  bank_opening: bl.bank.opening_movements, bank_period: bl.bank.period_movement, bank_closing: bl.bank.calculated_closing, bank_ledger: led.bank ?? '',
                  cash_received: rp.cash_received, cash_paid: rp.cash_paid, bank_received: rp.bank_received, bank_paid: rp.bank_paid, unattributed_paid: rp.unattributed_paid,
                  ib_paid_for_cash: ib.paid_for_other?.cash || 0, ib_paid_for_bank: ib.paid_for_other?.bank || 0,
                  ib_paid_by_cash: ib.paid_by_other?.cash || 0, ib_paid_by_bank: ib.paid_by_other?.bank || 0,
                  card_received_unsettled: rp.card_received || 0, platform_sales: rp.platform_sales || 0,
                  platform_settled_received_unverified: b.cash_position?.platform_receivable?.received_period || 0,
                  platform_commission: b.cash_position?.platform_receivable?.commission_period || 0,
                  platform_unverified_difference: b.cash_position?.platform_receivable?.unexplained_period || 0,
                  platform_outstanding: b.cash_position?.platform_receivable?.outstanding_calculated || 0 };
              }), `branch_cash_bank_${data?.period?.start || ''}_${data?.period?.end || ''}.csv`,
              ['branch', 'basis', 'cash_opening', 'cash_period', 'cash_closing', 'cash_ledger', 'bank_opening', 'bank_period', 'bank_closing', 'bank_ledger',
                'cash_received', 'cash_paid', 'bank_received', 'bank_paid', 'unattributed_paid', 'ib_paid_for_cash', 'ib_paid_for_bank', 'ib_paid_by_cash', 'ib_paid_by_bank',
                'card_received_unsettled', 'platform_sales', 'platform_settled_received_unverified', 'platform_commission', 'platform_unverified_difference', 'platform_outstanding']
                .map((k) => ({ key: k, label: k.replace(/_/g, ' ') })), 'branch cash/bank rows', 'cash_closing')}
              disabled={!data || loading}
              data-testid="export-branch-cash-bank-csv"
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-muted-foreground bg-white border border-border rounded-lg hover:bg-muted/50 disabled:opacity-50"
            >
              <FileText size={14} /> Cash &amp; Bank CSV
            </button>
          </div>
        </div>
        {(() => { const r = mode === 'month' ? getMonthRange(selYear, selMonth) : { start: startDate, end: endDate }; return (
          <GlAccountingStrip startDate={r.start} endDate={r.end} branchIds={branchIds} operational={data?.grand_total?.net} operationalLabel="Operational net (this report)" />
        ); })()}

        {/* Filters */}
        <Card data-tour="bs-filters"><CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-wrap">
            <div className="flex bg-muted rounded-lg p-0.5">
              <button onClick={() => setMode('month')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${mode === 'month' ? 'bg-white shadow text-foreground' : 'text-muted-foreground'}`} data-testid="mode-month">Month</button>
              <button onClick={() => setMode('range')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${mode === 'range' ? 'bg-white shadow text-foreground' : 'text-muted-foreground'}`} data-testid="mode-range">Date Range</button>
            </div>
            {mode === 'month' ? (
              <div className="flex items-center gap-1">
                <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-muted"><ChevronLeft size={16} /></button>
                <Select value={String(selMonth)} onValueChange={v => setSelMonth(parseInt(v))}><SelectTrigger className="w-[100px] h-8 text-xs"><SelectValue /></SelectTrigger><SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i)}>{m}</SelectItem>)}</SelectContent></Select>
                <Select value={String(selYear)} onValueChange={v => setSelYear(parseInt(v))}><SelectTrigger className="w-[80px] h-8 text-xs"><SelectValue /></SelectTrigger><SelectContent>{Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i).map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent></Select>
                <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-muted"><ChevronRight size={16} /></button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-36 h-8 text-xs" data-testid="start-date" />
                <span className="text-muted-foreground text-xs">to</span>
                <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-36 h-8 text-xs" data-testid="end-date" />
              </div>
            )}
            <DateQuickFilter defaultActive="" hideAll branchIds={branchIds}
              onFilterChange={(r) => { if (r?.start && r?.end) { setMode('range'); setStartDate(r.start); setEndDate(r.end); } }} />
            <div className="h-5 w-px bg-muted hidden sm:block" />
            <BranchMultiSelect value={branchIds} onChange={setBranchIds} branches={branches} testid="branch-filter" />
            {mode === 'range' && <Button size="sm" onClick={fetchData} className="h-8 text-xs ml-auto"><RefreshCw size={14} className="mr-1" />Apply</Button>}
          </div>
        </CardContent></Card>

        {loading ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !rows.length ? (
          <Card><CardContent className="p-12 text-center text-muted-foreground text-sm">No data for this period</CardContent></Card>
        ) : (
          <>
            <BranchOverview grand={grand} rows={rows} overview={data?.overview} names={branchNames} per={{ start: data?.period?.start, end: data?.period?.end, branchId: branchIds.length === 1 ? branchIds[0] : null }} />
            <AdvancedDetails>
            {/* Summary KPIs */}
            {grand && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-tour="bs-kpis">
                <KpiCard label="Total Sales (Incl. VAT)" value={fmtSR(grand.sales.total)} color="from-blue-500 to-indigo-600" raw={grand.sales.total} prevRaw={prevG?.sales?.total}
                  action={<CheckExportBtn disabled={!rows.length} testId="check-export-total-sales" title="Export per-branch sales breakdown"
                    onClick={() => handleExport(rows.map((b) => ({ branch: b.branch_name, cash: b.sales.cash, bank: b.sales.bank, card: b.sales.card || 0, online: b.sales.online, customer: b.sales.customer, total: b.sales.total })),
                      'total_sales_by_branch.csv',
                      [{ key: 'branch', label: 'Branch' }, { key: 'cash', label: 'Cash' }, { key: 'bank', label: 'Bank' }, { key: 'card', label: 'Card' }, { key: 'online', label: 'Platform' }, { key: 'customer', label: 'Customer' }, { key: 'total', label: 'Total Sales' }],
                      'branch sales rows', 'total')} />} />
                <KpiCard label="Cost of Goods" value={fmtSR(grand.cogs)} color="from-amber-500 to-orange-600" raw={grand.cogs} prevRaw={prevG?.cogs}
                  action={<CheckExportBtn disabled={!rows.length} testId="check-export-cogs" title="Export per-branch cost of goods"
                    onClick={() => handleExport(rows.map((b) => ({ branch: b.branch_name, cogs: b.cogs, balance: b.balance })),
                      'cost_of_goods_by_branch.csv',
                      [{ key: 'branch', label: 'Branch' }, { key: 'cogs', label: 'Cost of Goods' }, { key: 'balance', label: 'Balance' }],
                      'branch COGS rows', 'cogs')} />} />
                <KpiCard label="Net sales − COGS" value={fmtSR(grand.balance)} color="from-violet-500 to-fuchsia-600" raw={grand.balance} prevRaw={prevG?.balance} />
                <KpiCard label="Expenses" value={fmtSR(grand.expenses_total)} color="from-rose-500 to-red-600" raw={grand.expenses_total} prevRaw={prevG?.expenses_total} />
                <KpiCard label="Net" value={fmtSR(grand.net)} color={grand.net >= 0 ? 'from-emerald-500 to-teal-600' : 'from-red-500 to-rose-600'} sub={`${grand.net_margin}% margin`} raw={grand.net} prevRaw={prevG?.net} />
              </div>
            )}

            {grand?.cogs_breakdown && (grand.cogs_breakdown.paid_cash || grand.cogs_breakdown.paid_bank || grand.cogs_breakdown.unpaid_credit) ? (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs px-1 -mt-1" data-testid="company-cogs-breakdown">
                <span className="text-muted-foreground font-semibold uppercase text-xs tracking-wider">Network COGS Split:</span>
                {grand.cogs_breakdown.paid_cash > 0 && <span>Paid cash <b className="font-mono">{fmtSR(grand.cogs_breakdown.paid_cash)}</b></span>}
                {grand.cogs_breakdown.paid_bank > 0 && <span>Paid bank <b className="font-mono">{fmtSR(grand.cogs_breakdown.paid_bank)}</b></span>}
                {grand.cogs_breakdown.unpaid_credit > 0 && <span className="text-rose-700 dark:text-rose-300">Total outstanding <b className="font-mono">{fmtSR(grand.cogs_breakdown.unpaid_credit)}</b></span>}
              </div>
            ) : null}

            {grand && <MoneyPositionStrip grand={grand} verified={data?.overview?.cash_bank?.verified} />}

            <SupplierDebtTrend />

            {/* Comparison table */}
            <Card data-tour="bs-table">
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-[11px] xl:text-xs whitespace-nowrap" data-testid="branch-summary-table">
                  <thead className="bg-foreground text-white">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold sticky left-0 bg-foreground z-10">Branch</th>
                      <th className="text-right px-1.5 py-2 font-semibold bg-blue-900">Sales</th>
                      <th className="text-right px-1.5 py-2 font-semibold bg-amber-900">COGS</th>
                      <th className="text-right px-1.5 py-2 font-semibold bg-violet-900">Gross Profit</th>
                      <th className="text-right px-1.5 py-2 font-semibold bg-rose-900">Expenses</th>
                      <th className="text-right px-1.5 py-2 font-semibold bg-emerald-900">Net Profit</th>
                      <th className="text-right px-1.5 py-2 font-semibold">Margin</th>
                      <th className="text-right px-1.5 py-2 font-semibold border-l border-white/20" title="Other branches owe this branch (not profit)">IB Due From</th>
                      <th className="text-right px-1.5 py-2 font-semibold" title="This branch owes other branches (not profit)">IB Due To</th>
                      <th className="text-right px-1.5 py-2 font-semibold">Net IB</th>
                      <th className="text-right px-1.5 py-2 font-semibold border-l border-white/20" title="Calculated closing — opening + received − paid">Cash (calc.)</th>
                      <th className="text-right px-1.5 py-2 font-semibold" title="Calculated closing">Bank (calc.)</th>
                      <th className="text-right px-1.5 py-2 font-semibold" title="Platform sales not yet settled to bank">Platform Pend.</th>
                      <th className="text-right px-1.5 py-2 font-semibold" title="Last closed cashier count / bank statement balance at period end">Verified</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(b => <BranchDataRow key={b.branch_id} b={b} expenseGroups={expenseGroups} fmt={fmt} prevB={prev?.branches?.[b.branch_id]} hasPrev={!!prev} />)}
                    {grand && <BranchGrandTotalRow grand={grand} rows={rows} fmt={fmt} />}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* iter735 — Inter-Branch matrix summary. Sits between the
                comparison table and the per-branch detail cards so the
                accountant immediately sees "WHO owes WHOM, and what's
                left to settle" across the whole organization. */}
            <InterBranchMatrixCard rows={rows} />

            {/* Per-branch breakdown cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-tour="bs-detail">
              {rows.map(b => <BranchDetailCard key={b.branch_id} branch={b} groups={expenseGroups} periodLabel={periodLabel} startDate={startDate} endDate={endDate} period={period} prevBranch={prev?.branches?.[b.branch_id]} trend={trend?.branches?.[b.branch_id]} trendMonths={trend?.months} rank={salesRanks[b.branch_id]} />)}
            </div>
            </AdvancedDetails>
          </>
        )}
      </div>
      <FeatureTour key={replayKey} tourId="branch-summary-v2" steps={tourSteps} forceStart={forceTour} startDelay={2000} />
    </DashboardLayout>
  );
}

/** Tiny inline SVG sparkline — no chart lib needed. */
function Sparkline({ values = [], stroke = '#3b82f6', width = 72, height = 20, testId }) {
  const vals = values.map((v) => Number(v) || 0);
  if (vals.length < 2) return null;
  const max = Math.max(...vals, 0);
  const min = Math.min(...vals, 0);
  const range = max - min || 1;
  const step = width / (vals.length - 1);
  const pts = vals.map((v, i) => `${(i * step).toFixed(1)},${(height - 2 - ((v - min) / range) * (height - 4)).toFixed(1)}`);
  const last = pts[pts.length - 1].split(',');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} data-testid={testId} className="shrink-0">
      <polyline points={pts.join(' ')} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" opacity="0.9" />
      <circle cx={last[0]} cy={last[1]} r="1.8" fill={stroke} />
    </svg>
  );
}

/** 6-month sales/expense trend strip for a branch card header, with a
 *  hover tooltip listing the exact month-by-month figures. */
function TrendStrip({ trend, months = [] }) {
  const [open, setOpen] = useState(false);
  if (!trend) return null;
  const label = months.length ? `${months[0]} → ${months[months.length - 1]}` : '';
  return (
    <div
      className="relative flex items-center gap-2 bg-white/15 rounded-full px-2 py-0.5 cursor-help"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onClick={() => setOpen((v) => !v)}
      data-testid="trend-strip"
    >
      <Sparkline values={trend.sales} stroke="#bfdbfe" testId="spark-sales" />
      <Sparkline values={trend.expenses} stroke="#fecaca" testId="spark-expenses" />
      {open && (
        <div
          className="absolute top-full right-0 mt-1.5 z-50 w-60 rounded-lg border border-border bg-white shadow-xl p-2.5 text-left cursor-default"
          data-testid="trend-tooltip"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">6-Month Trend {label && `· ${label}`}</p>
          <table className="w-full text-xs tabular-nums border-collapse whitespace-nowrap [&_th]:bg-white [&_tr]:bg-white">
            <thead>
              <tr className="text-muted-foreground bg-white">
                <th className="text-left font-medium pb-1">Month</th>
                <th className="text-right font-medium pb-1"><span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 mr-1" />Sales</th>
                <th className="text-right font-medium pb-1"><span className="inline-block w-1.5 h-1.5 rounded-full bg-rose-500 mr-1" />Expenses</th>
              </tr>
            </thead>
            <tbody>
              {months.map((m, i) => (
                <tr key={m} className={`border-t border-border/40 ${i === months.length - 1 ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                  <td className="py-0.5">{m}</td>
                  <td className="py-0.5 text-right text-blue-700">{fmtSR(trend.sales?.[i] || 0)}</td>
                  <td className="py-0.5 text-right text-rose-700">{fmtSR(trend.expenses?.[i] || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Small rank medal based on monthly sales — gold/silver/bronze for top 3. */
function RankBadge({ rank }) {
  if (!rank) return null;
  const style = rank === 1
    ? 'bg-amber-300 text-amber-900 border-amber-100'
    : rank === 2
      ? 'bg-slate-200 text-slate-700 border-slate-50'
      : rank === 3
        ? 'bg-orange-300 text-orange-900 border-orange-100'
        : 'bg-white/20 text-white border-white/30';
  return (
    <span
      className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1 rounded-full text-xs font-bold border shadow-sm ${style}`}
      title={`Rank #${rank} by sales this period`}
      data-testid={`rank-badge-${rank}`}
    >
      #{rank}
    </span>
  );
}

const KPI_TONE = { 'from-blue-500': 'blue', 'from-amber-500': 'amber', 'from-violet-500': 'violet', 'from-rose-500': 'rose', 'from-emerald-500': 'emerald', 'from-red-500': 'rose' };
function KpiCard({ label, value, color, sub, raw, prevRaw, action }) {
  const hasPrev = prevRaw != null && Number(prevRaw) !== 0;
  const delta = hasPrev ? ((Number(raw || 0) - Number(prevRaw)) / Math.abs(Number(prevRaw))) * 100 : null;
  return (
    <StatCard label={label} value={value} tone={KPI_TONE[(color || '').split(' ')[0]] || 'slate'} action={action} sub={sub} testId={`kpi-${slug(label).toLowerCase().replace(/_/g, '-')}`}
      delta={prevRaw != null && (
        <span className="flex items-center gap-1" data-testid={`kpi-prev-${slug(label).toLowerCase().replace(/_/g, '-')}`}>
          <span>Prev {fmtSR(prevRaw)}</span>
          {delta != null && <span className={`font-semibold ${delta >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}%</span>}
        </span>
      )} />
  );
}

/** Profit ≠ cash ≠ verified money: one honest strip for the selected branches. */
function MoneyPositionStrip({ grand, verified }) {
  const c = grand.collections || {};
  const received = Number(c.cash || 0) + Number(c.bank || 0) + Number(c.card_awaiting_settlement || 0);
  const pending = Number(c.platform_sales || 0) + Number(c.customer_credit || 0);
  const other = Number(c.not_matched_to_payment || 0);  const vAny = verified && (verified.bank_total_verified != null || verified.cash_total_verified != null);
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="money-position-strip">
      <StatCard label="Earned net profit" tone="emerald" value={fmtSR(grand.net)} sub="earned basis — not cash" testId="mp-earned" />
      <StatCard label="Sales received" tone="teal" value={fmtSR(received)} sub="cash + bank/POS + card (POS/card awaits bank settlement)"
        status={Math.abs(other) >= 0.01 ? <StatusBadge kind="review" testId="mp-other">unresolved difference {fmtSR(other)} — review</StatusBadge> : null} testId="mp-received" />
      <StatCard label="Sales not yet received" tone="amber" value={fmtSR(pending)} sub={`platform ${fmtSR(c.platform_sales || 0)} · customer credit ${fmtSR(c.customer_credit || 0)}`}
        status={<StatusBadge kind="unverified">awaiting payout</StatusBadge>} testId="mp-pending" />
      <StatCard label="Verified cash + bank" tone="slate" value={vAny ? fmtSR((verified.bank_total_verified || 0) + (verified.cash_total_verified || 0)) : 'None verified'}
        sub="reconciled statements + closed cashier counts only"
        status={verified?.unverified_bank_accounts ? <StatusBadge kind="unverified">{verified.unverified_bank_accounts} bank account(s) UNVERIFIED</StatusBadge> : null} testId="mp-verified" />
    </div>
  );
}

/** (CheckExportBtn now lives in @/components/CheckExportBtn — shared app-wide.) */

/** Compact Month-over-Month comparison line shown under a total row. */
function MoMLine({ label, curVal, prevVal, testId }) {
  if (prevVal == null) return null;
  const hasPrev = Number(prevVal) !== 0;
  const delta = hasPrev ? ((Number(curVal || 0) - Number(prevVal)) / Math.abs(Number(prevVal))) * 100 : null;
  return (
    <div className="px-6 py-1 bg-slate-50/60 dark:bg-slate-900/20 text-xs text-muted-foreground flex items-center gap-2 border-t border-border/50" data-testid={testId}>
      <span className="uppercase tracking-wider font-semibold text-xs">{label}</span>
      <span className="font-mono">{fmtSR(prevVal)}</span>
      {delta != null && (
        <span className={`font-semibold ${delta >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}% vs prev</span>
      )}
    </div>
  );
}

function BranchDetailCard({ branch: b, groups, periodLabel, startDate, endDate, period, prevBranch, trend, trendMonths, rank }) {
  const cardRef = useRef(null);
  const [ibAll, setIbAll] = useState(null);
  const inView = useInView(cardRef, { once: true, margin: '200px' }); // owner position loads only when this branch is on screen
  const op = useOwnerPosition(b.branch_id, startDate || period?.start, endDate || period?.end, inView);
  const [cogsTab, setCogsTab] = useState(null);
  const sx = salesExtras(op);
  const [ibLoading, setIbLoading] = useState(false);
  const loadIbAll = async () => {
    if (ibAll || ibLoading) return;
    setIbLoading(true);
    try {
      const { data } = await api.get('/reports/inter-branch/position', { params: { branch_id: b.branch_id } });
      setIbAll(data || {});
    } catch { setIbAll({}); }
    finally { setIbLoading(false); }
  };
  const handleCopy = () => {
    if (!cardRef.current) return;
    const safeName = (b.branch_name || 'branch').replace(/[^\w-]+/g, '_');
    copyElementAsImage(cardRef.current, { filename: `${safeName}_${periodLabel || 'summary'}.png`.replace(/\s+/g, '_') });
  };

  // "Check & Export" — reuses lib/exportCsv (same as inter-branch + COGS-audit exports).
  const checkExportExpenses = async () => {
    try {
      const r = await api.get('/expenses', { params: { start_date: period?.start, end_date: period?.end, branch_id: b.branch_id, limit: 5000 } });
      handleExport(r.data?.data || [], `expenses_${slug(b.branch_name)}_${period?.start || ''}.csv`, [
        { key: 'date', label: 'Date', format: (v) => String(v || '').slice(0, 10) },
        { key: 'category', label: 'Category' },
        { key: 'description', label: 'Description' },
        { key: 'supplier_name', label: 'Supplier' },
        { key: 'payment_mode', label: 'Payment Mode' },
        { key: 'amount', label: 'Amount' },
      ], 'expense records');
    } catch (e) { toast.error(`Export failed (HTTP ${e.response?.status ?? 'network'}): ${e.response?.data?.detail || e.message || 'unknown error'}`); }
  };

  return (
    <Card data-testid={`branch-detail-${b.branch_id}`}>
      <div ref={cardRef} className="bg-white rounded-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-4 py-2.5 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2"><RankBadge rank={rank} />{b.branch_name}</h3>
          <div className="flex items-center gap-2">
            <TrendStrip trend={trend} months={trendMonths} />
            {b.pct && <span className="text-xs text-white/90 font-medium bg-white/15 px-2 py-0.5 rounded-full" title="Share of grand total Sales">{b.pct.sales}% of sales</span>}
            {periodLabel && <span className="text-xs text-white/80 font-medium">{periodLabel}</span>}
          </div>
        </div>
        <div className="p-0 text-xs">
          <Section title="Sales by payment mode" hint={`total ${fmtSR(b.sales.total)}`} testId={`sec-sales-${b.branch_id}`}>
            <Row label="Cash Sales" value={fmtSR(b.sales.cash)} indent extra={sx.cash} />
            <Row label="Bank / POS terminal Sales" value={fmtSR(b.sales.bank)} indent extra={sx.bank} testId={`sales-bank-${b.branch_id || 'unassigned'}`} />
            <Row label="Card" value={fmtSR(b.sales.card || 0)} indent extra={b.sales.card ? sx.card : undefined} testId={`sales-card-${b.branch_id || 'unassigned'}`} />
            <Row label="Platform Sales" value={fmtSR(b.sales.online)} indent extra={sx.online} testId={`sales-online-${b.branch_id || 'unassigned'}`} />
            <Row label="Customer (Credit)" value={fmtSR(b.sales.customer)} indent extra={sx.customer} testId={`sales-customer-${b.branch_id || 'unassigned'}`} />
          </Section>
          <Row label="Total Sales (incl. VAT)" value={fmtSR(b.sales.total)} bold bg="bg-info/10" extra={b.pct ? `${b.pct.sales}%` : undefined} />
          <div className="px-6 py-1 text-[11px] text-muted-foreground flex flex-wrap gap-x-4 border-t border-border/50" data-testid={`sales-vat-split-${b.branch_id}`}>
            <span>Excl. VAT <b className="font-mono text-foreground">{fmtSR(b.sales.net ?? (b.sales.total - (b.sales.vat || 0)))}</b></span>
            <span>Output VAT <b className="font-mono text-foreground">{fmtSR(b.sales.vat || 0)}</b></span>
            <span>Incl. VAT <b className="font-mono text-foreground">{fmtSR(b.sales.total)}</b></span>
            <span className="italic">by payment method · collections shown separately below (never added twice)</span>
          </div>
          <MoMLine label="Prev Month Sales" curVal={b.sales.total} prevVal={prevBranch?.sales?.total} testId={`mom-sales-${b.branch_id}`} />
          <CogsDrillRow b={b} startDate={startDate || period?.start} endDate={endDate || period?.end} onOpen={() => setCogsTab('all')} />
          <CogsDetailDialog b={b} start={startDate || period?.start} end={endDate || period?.end} tab={cogsTab} onTab={setCogsTab} onClose={() => setCogsTab(null)} />
          {(b.sales?.total > 0 || (b.cogs_breakdown && (b.cogs_breakdown.paid_cash || b.cogs_breakdown.paid_bank || b.cogs_breakdown.unpaid_credit))) ? (
            <Section title="COGS split & supplier payments" hint={b.sales?.total > 0 ? `${((b.cogs / b.sales.total) * 100).toFixed(2)}% of sales` : ''} testId={`sec-cogs-${b.branch_id}`}>
              {b.sales?.total > 0 && (
                <div className="px-6 py-0.5 text-[11px] text-muted-foreground flex justify-between" data-testid={`cogs-pct-${b.branch_id}`}>
                  <span>COGS as % of sales</span><span className="font-mono">{((b.cogs / b.sales.total) * 100).toFixed(2)}%</span>
                </div>
              )}
              {b.cogs_breakdown && (b.cogs_breakdown.paid_cash || b.cogs_breakdown.paid_bank || b.cogs_breakdown.unpaid_credit) ? (
                <div className="px-6 py-1.5 bg-amber-50/40 dark:bg-amber-950/5 text-xs text-amber-900 dark:text-amber-300 flex flex-wrap gap-x-3 gap-y-0.5 border-t border-amber-100 dark:border-amber-900/30" data-testid={`cogs-split-${b.branch_id}`}>
                  {b.cogs_breakdown.paid_cash > 0 && <button type="button" onClick={() => setCogsTab('cash')} className="hover:underline" data-testid={`cogs-paid-cash-${b.branch_id}`}>Paid cash <b className="font-mono">{fmtSR(b.cogs_breakdown.paid_cash)}</b></button>}
                  {b.cogs_breakdown.paid_bank > 0 && <button type="button" onClick={() => setCogsTab('bank')} className="hover:underline" data-testid={`cogs-paid-bank-${b.branch_id}`}>Paid bank <b className="font-mono">{fmtSR(b.cogs_breakdown.paid_bank)}</b></button>}
                  {b.cogs_breakdown.unpaid_credit > 0 && <button type="button" onClick={() => setCogsTab('credit')} className="text-rose-700 dark:text-rose-300 hover:underline" data-testid={`cogs-credit-${b.branch_id}`}>On credit this period <b className="font-mono">{fmtSR(b.cogs_breakdown.unpaid_credit)}</b></button>}
                  {op?.supplier_bills?.payment_views && (
                    <span className="text-muted-foreground" data-testid={`cogs-payments-${b.branch_id}`}>· Supplier payments (not COGS, do not add):{' '}
                      paid by this branch <b className="font-mono">{fmtSR(op.supplier_bills.payment_views.paid_by_branch.total)}</b> ·{' '}
                      <button type="button" onClick={() => setCogsTab('payments')} className="hover:underline" data-testid={`cogs-payments-applied-${b.branch_id}`}>applied to this branch's bills <b className="font-mono">{fmtSR(op.supplier_bills.payment_views.applied_to_branch_bills.total)}</b></button>
                    </span>
                  )}
                </div>
              ) : null}
            </Section>
          ) : null}
          <Row label="Balance" value={fmtSR(b.balance)} bold bg="bg-violet-50" color="text-violet-800" />
          <Section title="Expenses by group" hint={`total ${fmtSR(b.expenses_total)}`} testId={`sec-expenses-${b.branch_id}`}
            action={<CheckExportBtn onClick={checkExportExpenses} testId={`check-export-expenses-${b.branch_id}`} title="Validate this branch's expense rows for the period, then export CSV" />}>
            <ExpenseGroupRows b={b} groups={groups} period={period} start={startDate || period?.start} end={endDate || period?.end} />
            {b.expenses_breakdown && (b.expenses_breakdown.paid_cash || b.expenses_breakdown.paid_bank || b.expenses_breakdown.unpaid_credit) ? (
              <div className="px-6 py-1.5 bg-rose-50/40 dark:bg-rose-950/5 text-xs text-rose-900 dark:text-rose-300 flex flex-wrap gap-x-3 gap-y-0.5 border-t border-rose-100 dark:border-rose-900/30" data-testid={`exp-split-${b.branch_id}`}>
                {b.expenses_breakdown.paid_cash > 0 && <span>Paid cash <b className="font-mono">{fmtSR(b.expenses_breakdown.paid_cash)}</b></span>}
                {b.expenses_breakdown.paid_bank > 0 && <span>Paid bank <b className="font-mono">{fmtSR(b.expenses_breakdown.paid_bank)}</b></span>}
                {b.expenses_breakdown.unpaid_credit > 0 && <span className="text-rose-800 dark:text-rose-200 font-semibold">Unpaid credit <b className="font-mono">{fmtSR(b.expenses_breakdown.unpaid_credit)}</b></span>}
              </div>
            ) : null}
          </Section>
          <Row label="Total Expenses" value={`(${fmtSR(b.expenses_total)})`} bold bg="bg-error/10" color="text-error" extra={b.pct ? `${b.pct.expenses}%` : undefined} />
          <MoMLine label="Prev Month Expenses" curVal={b.expenses_total} prevVal={prevBranch?.expenses_total} testId={`mom-expenses-${b.branch_id}`} />
          <Row
            label={b.net >= 0 ? 'Net Profit' : 'Net Loss'}
            value={fmtSR(b.net)}
            bold
            bg={b.net >= 0 ? 'bg-success/10' : 'bg-error/10'}
            color={b.net >= 0 ? 'text-success' : 'text-error'}
            extra={`${b.net_margin}% margin`}
          />
          {b.branch_id !== '__unassigned__' && b.cash_position && (
            <Section title="Inter-branch — who paid for whom" hint={`net this period ${fmtSR(b.cash_position.inter_branch_net_outstanding)} · not profit`} testId={`sec-ib-${b.branch_id}`} defaultOpen>
              <InterBranchSection b={b} ibAll={ibAll} loadIbAll={loadIbAll} ibLoading={ibLoading} op={op} />
            </Section>
          )}
          {b.branch_id !== '__unassigned__' && <FinancialPosition b={b} op={op} ibAll={ibAll} />}
          {b.branch_id !== '__unassigned__' && (
            <Section title="Sales — received vs not received" hint="cash, card, platforms, customers" testId={`sec-collections-${b.branch_id}`}>
              <CollectionsSection b={b} op={op} />
            </Section>
          )}
          {b.branch_id !== '__unassigned__' && (
            <Section title="Purchases & supplier bills" hint="current vs older bills" testId={`sec-supplier-bills-${b.branch_id}`}>
              <SupplierBillsSection b={b} op={op} />
            </Section>
          )}
          {b.branch_id !== '__unassigned__' && (
            <Section title="Cash & Bank position" hint="opening + received − paid = calculated closing · UNVERIFIED until matched to a cash count / bank statement" testId={`sec-cbpos-${b.branch_id}`} defaultOpen>
              <CashBankPositionTable b={b} op={op} />
            </Section>
          )}
          {b.cash_position && (
            <Section title="Cash flow details & ledger comparison" hint="calculated, not verified" testId={`sec-cashbank-${b.branch_id}`}>
              <Row label="Cash movement this period (received − paid)" value={fmtSR(b.cash_position.cash_in_hand)}
                color={b.cash_position.cash_in_hand >= 0 ? 'text-success' : 'text-error'} indent />
              <CashFlowBreakdown bd={b.cash_position.cash_breakdown} mode="cash" branchId={b.branch_id} startDate={startDate || period?.start} endDate={endDate || period?.end} />
              <Row label="Bank movement this period (received − paid)" value={fmtSR(b.cash_position.bank_in_hand)}
                color={b.cash_position.bank_in_hand >= 0 ? 'text-info' : 'text-error'} indent />
              <CashFlowBreakdown bd={b.cash_position.bank_breakdown} mode="bank" branchId={b.branch_id} startDate={startDate || period?.start} endDate={endDate || period?.end} />
              <Row label="Total cash + bank movement" value={fmtSR(b.cash_position.total_liquid)} bold
                color={b.cash_position.total_liquid >= 0 ? 'text-success' : 'text-error'} />
              {prevBranch?.cash_position && (
                <div className="px-6 py-1 text-xs text-muted-foreground flex flex-wrap gap-x-3 border-t border-border/50" data-testid={`mom-cash-${b.branch_id}`}>
                  <span className="font-semibold">Prev month movement</span>
                  <span>Cash <b className="font-mono">{fmtSR(prevBranch.cash_position.cash_in_hand)}</b></span>
                  <span>Bank <b className="font-mono">{fmtSR(prevBranch.cash_position.bank_in_hand)}</b></span>
                </div>
              )}
              {b.cash_position.customer_receivable > 0 && <Row label="Due from customers (credit sales)" value={fmtSR(b.cash_position.customer_receivable)} indent color="text-warning" />}
              {b.cash_position.capital_invested > 0 && <Row label="Capital invested" value={fmtSR(b.cash_position.capital_invested)} indent color="text-purple-700" />}
              <p className="px-4 py-1.5 text-[10px] text-muted-foreground border-t border-border/50">These are movements for the selected period, not Cash in Hand or Bank in Hand. No verified closing balance is available until cash counts / bank statements are reconciled.</p>
              <div className="p-3"><BranchBalancesPanel b={b} startDate={startDate || period?.start} endDate={endDate || period?.end} hidePlatform /></div>
            </Section>
          )}
          {b.branch_id !== '__unassigned__' && (
            <Section title="Profit check — this page vs General Ledger" hint="explains the difference; nothing adjusted" testId={`sec-profit-check-${b.branch_id}`}>
              <ProfitCheck b={b} op={op} />
            </Section>
          )}
          {b.cash_position?.platform_receivable && (
            <Section title="Platform receivables" hint={`outstanding ${fmtSR(b.cash_position.platform_receivable.outstanding_calculated)}`} testId={`sec-platform-${b.branch_id}`}>
              <PlatformSection b={b} />
            </Section>
          )}
          <Section title="Overdue & due-soon bills" hint="as of period end" testId={`sec-bills-cf-${b.branch_id}`} lazy>
            <BillsDueSoon branchId={b.branch_id} branchName={b.branch_name} asOf={endDate || period?.end} />
          </Section>
          {b.branch_id !== '__unassigned__' && (
            <Section title="Cash flow statement" testId={`sec-cfs-${b.branch_id}`} lazy>
              <CashFlowStatement branchId={b.branch_id} startDate={startDate || period?.start} endDate={endDate || period?.end} netIncome={b.net} />
            </Section>
          )}
        </div>
      </div>
      <div className="px-4 py-2 border-t border-border flex justify-end">
        <button
          onClick={handleCopy}
          data-testid={`copy-image-${b.branch_id}`}
          title="Copy card as image to paste into WhatsApp, Slack or email"
          className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-muted-foreground bg-muted/50 hover:bg-muted border border-border rounded-md transition"
        >
          <ImageIcon size={12} /> Copy as Image
        </button>
      </div>
    </Card>
  );
}

function Row({ label, value, indent, bold, bg = '', color = 'text-foreground', extra, testId }) {
  return (
    <div className={`flex justify-between items-center px-4 py-2 border-t border-border ${bg}`} data-testid={testId}>
      <span className={`${indent ? 'pl-3 text-muted-foreground' : ''} ${bold ? 'font-semibold' : ''} ${color}`}>{label}</span>
      <div className="flex items-center gap-2">
        {extra && <span className="text-xs text-muted-foreground">{extra}</span>}
        <span className={`font-mono ${bold ? 'font-bold' : ''} ${color}`}>{value}</span>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Table row sub-components (iter385)
// -----------------------------------------------------------------------------
// Lifted out of inline map()s inside <tbody> so Emergent dev-tool's
// instrumentation <span> wraps the component's top-level <tr>, not the
// tbody→span→tr hydration-invalid chain. Same fix we applied on
// CustomerReportsPage — silences the "<span> cannot be a child of <tbody>"
// console warning across the Branch Summary page.

// Inter-branch balances are balance-sheet items (due from / due to) and never enter profit.
const ibPos = (b) => {
  const cp = b?.cash_position?.inter_branch_counterparties || {};
  const from = (cp.receivables || []).reduce((t, r) => t + (Number(r.balance) || 0), 0);
  const to = (cp.payables || []).reduce((t, r) => t + (Number(r.balance) || 0), 0);
  return { from, to, net: from - to };
};
const posOf = (b) => ({
  cash: b?.balances?.cash?.calculated_closing ?? null, bank: b?.balances?.bank?.calculated_closing ?? null,
  platform: b?.cash_position?.platform_receivable?.outstanding_calculated ?? null, verified: b?.balances?.verified || null,
});
const Num = ({ v, fmt, cls = '', neg }) => <td className={`px-1.5 py-1.5 text-right tabular-nums ${cls}`}>{v == null ? '—' : neg ? `(${fmt(v)})` : fmt(v)}</td>;

function BranchDataRow({ b, expenseGroups, fmt, prevB, hasPrev }) {
  const [open, setOpen] = useState(false);
  const ib = ibPos(b), ps = posOf(b), v = ps.verified;
  const id = b.branch_id || 'unassigned';
  return (
    <>
      <tr className="border-t border-border hover:bg-muted/50 cursor-pointer" onClick={() => setOpen((o) => !o)} data-testid={`row-toggle-${id}`}>
        <td className="px-3 py-1.5 font-medium text-foreground sticky left-0 bg-white whitespace-nowrap" data-testid={`row-${b.branch_id}`}>
          <ChevronRight size={12} className={`inline mr-1 transition-transform ${open ? 'rotate-90' : ''}`} />{b.branch_name}
        </td>
        <Num v={b.sales.total} fmt={fmt} cls="font-semibold bg-info/10" />
        <Num v={b.cogs} fmt={fmt} neg cls="text-warning bg-warning/10" />
        <Num v={b.balance} fmt={fmt} cls="font-semibold bg-violet-50/40" />
        <td className="px-1.5 py-1.5 text-right tabular-nums text-error bg-error/10" data-testid={`row-exp-${id}`}
          title={b.inter_branch_adjustment ? `Paid by this branch ${fmt(b.expenses_total)} · inter-branch cost reallocation ${fmt(-b.inter_branch_adjustment)}` : undefined}>
          ({fmt(b.expenses_total - (b.inter_branch_adjustment || 0))}){b.inter_branch_adjustment ? <sup className="text-amber-700">*</sup> : null}
        </td>
        <td className={`px-2 py-1.5 text-right tabular-nums font-bold ${b.net >= 0 ? 'text-success bg-success/10' : 'text-error bg-error/10'}`} data-testid={`row-net-${id}`}>{fmt(b.net)}</td>
        <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">{b.net_margin}%</td>
        <Num v={ib.from} fmt={fmt} cls="border-l border-border text-amber-800" />
        <Num v={ib.to} fmt={fmt} cls="text-amber-800" />
        <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${ib.net >= 0 ? 'text-amber-800' : 'text-rose-700'}`} data-testid={`row-ib-net-${id}`}>{fmt(ib.net)}</td>
        <Num v={ps.cash} fmt={fmt} cls="border-l border-border" />
        <Num v={ps.bank} fmt={fmt} />
        <Num v={ps.platform} fmt={fmt} cls="text-purple-700" />
        <td className="px-1.5 py-1.5 text-right tabular-nums whitespace-nowrap" data-testid={`row-verified-${id}`}
          title={v ? `Cash: ${v.cash != null ? `counted ${v.cash_as_of}` : 'no closed cashier count'} · Bank: ${v.bank != null ? 'statement balance at period end' : v.bank_last != null ? `last statement ${fmt(v.bank_last)} on ${v.bank_as_of}` : 'no statement uploaded'}` : ''}>
          {v ? <>{v.cash != null ? fmt(v.cash) : <span className="text-muted-foreground">—</span>} · {v.bank != null ? fmt(v.bank) : <span className="text-muted-foreground">—</span>}</> : '—'}
        </td>
      </tr>
      {open && (
        <tr className="bg-muted/30" data-testid={`row-detail-${id}`}>
          <td colSpan={14} className="px-4 py-2 text-[11px] text-muted-foreground whitespace-normal">
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <span className="font-semibold text-foreground">Sales by mode:</span>
              <span>Cash {fmt(b.sales.cash)}</span><span>Bank/POS {fmt(b.sales.bank)}</span><span data-testid={`table-card-${id}`}>Card {fmt(b.sales.card || 0)}</span>
              <span>Platform {fmt(b.sales.online)}</span><span>Customer credit {fmt(b.sales.customer)}</span>
              {hasPrev && <span>Prev sales {fmt(prevB?.sales?.total || 0)} · Prev expenses {fmt(prevB?.expenses_total || 0)}</span>}
            </div>
            {!!b.inter_branch_adjustment && (
              <div className="mt-1 text-amber-800" data-testid={`row-ib-realloc-${id}`}>
                * Expenses = paid by this branch {fmt(b.expenses_total)} {b.inter_branch_adjustment < 0 ? '+' : '−'} {fmt(Math.abs(b.inter_branch_adjustment))} costs another branch paid for / was paid for (profit reallocation; the due-from / due-to balance itself is not profit).
              </div>
            )}
            {expenseGroups.length > 0 && (
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                <span className="font-semibold text-foreground">Expenses by group:</span>
                {expenseGroups.map((g) => <span key={g}>{g} {fmt(b.expenses_by_group?.[g] || 0)}</span>)}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * iter735 — Org-wide Inter-Branch Matrix card. Aggregates every
 * branch row's `cash_position.inter_branch_counterparties` into one
 * "who-owes-whom" matrix so the accountant gets a single-screen
 * answer to "what's the inter-branch position right now?".
 *
 * - Top KPI row: total receivables, total payables, NET (should net
 *   to zero across all branches because every payable on side A is
 *   a receivable on side B). Non-zero net usually means data-entry
 *   mismatch — surface it so the accountant can investigate.
 * - Below: A simple table sorted by outstanding balance desc.
 */
function InterBranchMatrixCard({ rows }) {
  const matrix = [];
  let totalRecv = 0;
  let totalPay = 0;
  (rows || []).forEach(b => {
    const cp = b?.cash_position?.inter_branch_counterparties;
    if (!cp) return;
    (cp.receivables || []).forEach(r => {
      if (Math.abs(r.balance) < 0.01) return;
      totalRecv += r.balance;
      // b's receivable from r: b paid for r → From (payer) = b, To (beneficiary, still owes) = r.
      matrix.push({
        from_id: b.branch_id,
        from_name: b.branch_name,
        to_id: r.branch_id,
        to_name: r.branch_name,
        gross: r.gross,
        settled: r.settled,
        balance: r.balance,
      });
    });
  });
  // sum payables for KPI (each payable is also a receivable on the other branch — but we sum payables independently for "this branch needs to pay" total)
  (rows || []).forEach(b => {
    const cp = b?.cash_position?.inter_branch_counterparties;
    (cp?.payables || []).forEach(p => { totalPay += p.balance; });
  });
  if (matrix.length === 0) {
    return (
      <Card className="border-amber-200 bg-amber-50/30" data-testid="inter-branch-matrix-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-semibold text-sm text-amber-900 inline-flex items-center gap-2">
              🔄 Inter-Branch Matrix
              <span className="text-xs font-normal text-amber-700/80 bg-amber-100 px-2 py-0.5 rounded-full">who owes whom · period view</span>
            </h3>
            <CheckExportBtn disabled onClick={() => {}} testId="inter-branch-export-btn" title="Validate inter-branch balances, then export CSV" />
          </div>
          <p className="text-xs text-muted-foreground mt-2" data-testid="inter-branch-empty-msg">No inter-branch balances for this period</p>
        </CardContent>
      </Card>
    );
  }
  matrix.sort((a, b) => b.balance - a.balance);
  const net = totalRecv - totalPay;
  return (
    <Card className="border-amber-200 bg-amber-50/30" data-testid="inter-branch-matrix-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-semibold text-sm text-amber-900 inline-flex items-center gap-2">
            🔄 Inter-Branch Matrix
            <span className="text-xs font-normal text-amber-700/80 bg-amber-100 px-2 py-0.5 rounded-full">
              who owes whom · period view
            </span>
          </h3>
          <div className="flex items-center gap-3 text-xs">
            <span className="text-teal-700">Total receivable <b className="font-mono">{fmtSR(totalRecv)}</b></span>
            <span className="text-orange-700">Total payable <b className="font-mono">{fmtSR(totalPay)}</b></span>
            <span className={Math.abs(net) < 0.5 ? 'text-muted-foreground' : 'text-rose-700 font-semibold'}>
              Net <b className="font-mono">{fmtSR(net)}</b>
              {Math.abs(net) >= 0.5 && <span className="ml-1 text-xs" title="Receivables should equal Payables across all branches; non-zero usually means a cross-branch expense was tagged on only one side. Click into the affected branch's detail card to investigate.">⚠</span>}
            </span>
            <CheckExportBtn
              onClick={() => handleExport(
                matrix.map((m) => ({
                  counterparty: `${m.from_name} → ${m.to_name}`,
                  gross: m.gross,
                  settled: m.settled,
                  balance: m.balance,
                })),
                'inter_branch_matrix.csv',
                [
                  { key: 'counterparty', label: 'Counterparty' },
                  { key: 'gross', label: 'Gross Amount' },
                  { key: 'settled', label: 'Settled' },
                  { key: 'balance', label: 'Outstanding Balance' },
                ],
                'inter-branch balances', 'balance',
              )}
              testId="inter-branch-export-btn"
              title="Validate inter-branch balances, then export CSV"
            />
          </div>
        </div>
        <div className="overflow-x-auto rounded border border-amber-200/70 bg-white">
          <table className="w-full text-xs" data-testid="inter-branch-matrix-table">
            <thead className="bg-amber-100/60 text-xs uppercase tracking-wider text-amber-900">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">From (payer)</th>
                <th className="text-left px-3 py-2 font-semibold">To (beneficiary)</th>
                <th className="text-right px-3 py-2 font-semibold">Gross</th>
                <th className="text-right px-3 py-2 font-semibold">Settled</th>
                <th className="text-right px-3 py-2 font-semibold">Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {matrix.map(m => (
                <tr key={`${m.from_id}-${m.to_id}`} className="border-t border-amber-100/50" data-testid={`matrix-row-${m.from_id}-${m.to_id}`}>
                  <td className="px-3 py-1.5 text-teal-800 font-medium">{m.from_name}</td>
                  <td className="px-3 py-1.5 text-orange-800 font-medium">{m.to_name}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">{fmtSR(m.gross)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">{m.settled > 0 ? `−${fmtSR(m.settled)}` : '—'}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-amber-900">{fmtSR(m.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-amber-700/80 mt-2 leading-relaxed">
          Reads as: <b>From</b> branch paid an expense / invoice / supplier bill on behalf of <b>To</b> branch.
          The outstanding column is what <b>To</b> still has to repay <b>From</b>. Drill into a specific branch&apos;s detail card below to see its full Receivables / Payables list with the source documents.
        </p>
      </CardContent>
    </Card>
  );
}

function BranchGrandTotalRow({ grand, rows, fmt }) {
  const sum = (f) => (rows || []).reduce((t, r) => { const x = f(r); return x == null ? t : t + x; }, 0);
  const ibF = sum((r) => ibPos(r).from), ibT = sum((r) => ibPos(r).to);
  return (
    <tr className="border-t-2 border-stone-900 bg-muted/50 font-bold" data-testid="grand-total-row">
      <td className="px-3 py-2 text-foreground sticky left-0 bg-muted/50">GRAND TOTAL</td>
      <Num v={grand.sales.total} fmt={fmt} cls="bg-info/20" />
      <Num v={grand.cogs} fmt={fmt} neg cls="text-warning bg-warning/20" />
      <Num v={grand.balance} fmt={fmt} cls="bg-violet-100" />
      <Num v={grand.expenses_total - sum((r) => r.inter_branch_adjustment || 0)} fmt={fmt} neg cls="text-error bg-error/20" />
      <td className={`px-2 py-2 text-right tabular-nums ${grand.net >= 0 ? 'text-success bg-success/20' : 'text-error bg-error/20'}`} data-testid="grand-net">{fmt(grand.net)}</td>
      <td className="px-2 py-2 text-right tabular-nums">{grand.net_margin}%</td>
      <Num v={ibF} fmt={fmt} cls="border-l border-border" />
      <Num v={ibT} fmt={fmt} />
      <td className="px-2 py-2 text-right tabular-nums" title="Company-wide this nets to ~0 (one branch's due-from is another's due-to)" data-testid="grand-ib-net">{fmt(ibF - ibT)}</td>
      <Num v={sum((r) => posOf(r).cash)} fmt={fmt} cls="border-l border-border" />
      <Num v={sum((r) => posOf(r).bank)} fmt={fmt} />
      <Num v={sum((r) => posOf(r).platform)} fmt={fmt} />
      <td className="px-2 py-2 text-right text-[11px] font-normal text-muted-foreground">per branch</td>
    </tr>
  );
}

function CogsSupplierRow({ s }) {
  return (
    <tr className="border-t border-border/50">
      <td className="p-2">{s.supplier_name}</td>
      <td className="p-2 text-right text-warning">{fmtSR(s.expense_total)}</td>
      <td className="p-2 text-right text-info">{fmtSR(s.payment_total)}</td>
      <td className={`p-2 text-right font-semibold ${s.net_owed > 0 ? 'text-error' : 'text-success'}`}>{fmtSR(s.net_owed)}</td>
    </tr>
  );
}

function CogsExpenseRow({ e }) {
  return (
    <tr className="border-t border-border/50">
      <td className="p-2 text-xs">{String(e.date || '').slice(0, 10)}</td>
      <td className="p-2 text-xs">{e.category || '-'}</td>
      <td className="p-2 text-xs">{e.description || '-'}</td>
      <td className="p-2 text-right text-warning">{fmtSR(e.amount)}</td>
    </tr>
  );
}

function CogsEmptyRow({ colSpan, label }) {
  return <tr><td colSpan={colSpan} className="p-4 text-center text-muted-foreground">{label}</td></tr>;
}

/**
 * CashFlowBreakdown (iter382) — indented sub-rows under Cash/Bank in Hand
 * explaining where the money went. Collapsed by default so the main card
 * stays tidy; click the small "show breakdown" chevron to expand.
 */
function CashFlowBreakdown({ bd, mode, branchId, startDate, endDate /* 'cash' | 'bank' */ }) {
  const [open, setOpen] = useState(false);
  // iter395d/395e — Audit panel: stores which audit kind is open
  // ('cogs' for stock purchases, 'settlements' for old credit bills, or
  // null when closed). One state replaces the two we'd otherwise need.
  const [auditOpen, setAuditOpen] = useState(null);
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditErr, setAuditErr] = useState(null);
  const loadAudit = async () => {
    if (auditData || auditLoading) return;
    if (!branchId) { setAuditErr('No branch selected'); return; }
    setAuditLoading(true);
    setAuditErr(null);
    try {
      const params = { branch_id: branchId };
      if (startDate) params.start_date = startDate;
      if (endDate)   params.end_date = endDate;
      const { data: d } = await api.get('/accounting/branch-cogs-detail', { params });
      setAuditData(d);
    } catch (e) {
      setAuditErr(e?.response?.data?.detail || e?.message || 'Failed to load detail');
    } finally { setAuditLoading(false); }
  };
  if (!bd) return null;
  const inLabel = mode === 'cash' ? 'Cash In — Cash Sales this month' : 'Bank In — Bank + Online Sales this month';
  const outs = [
    ['Salary / Rent / Utility / Other expenses',  bd.operating_expenses, `non-supplier expenses paid in ${mode} (salary, rent, gov fee, utility, etc.)`,  null],
    ['Stock Purchases paid this month',           bd.cogs_expenses,      `supplier-purchase expenses booked AND paid this period in ${mode}`,           'cogs'],
    ['Settlements of old credit bills',           bd.supplier_payments,  `${mode} paid against earlier credit supplier invoices — NOT this month's purchases`, 'settlements'],
    ['Capital Expenses',                          bd.capital_expenses,   'one-time asset / equipment purchases',                                       null],
  ].filter(([, v]) => v && v > 0);
  if (outs.length === 0 && !bd.cash_in && !bd.bank_in) return null;
  // Filter audit payments to the matching payment_mode.
  const cashModes = new Set(['cash', 'petty']);
  const bankModes = new Set(['bank', 'transfer', 'bank_transfer', 'online', 'card', 'debit']);
  const filteredPayments = (auditData?.payments || []).filter((p) => {
    const m = (p.payment_mode || 'cash').toLowerCase();
    return mode === 'cash' ? cashModes.has(m) : bankModes.has(m);
  });
  // iter395e — Stock-purchases audit reuses the same /branch-cogs-detail
  // payload (`expenses` array). We filter to only the cash/bank-paid rows
  // for the active mode (credit-mode rows roll up under "Settlements"
  // when paid later, not under "Stock Purchases paid this month").
  const filteredCogsExpenses = (auditData?.expenses || []).filter((e) => {
    const m = (e.payment_mode || '').toLowerCase();
    return mode === 'cash' ? cashModes.has(m) : bankModes.has(m);
  });
  return (
    <div className="px-4 py-1 bg-muted/20 border-t border-border">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
        data-testid={`${mode}-flow-toggle`}
      >
        <span>{open ? '▾' : '▸'} {open ? 'Hide' : 'Show'} {mode} flow breakdown</span>
      </button>
      {open && (
        <div className="mt-1.5 pl-3 space-y-0.5 pb-1.5 text-xs font-mono">
          <div className="flex justify-between py-0.5">
            <span className="text-muted-foreground">{inLabel}</span>
            <span className="text-success">+{(bd[mode === 'cash' ? 'cash_in' : 'bank_in'] || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
          </div>
          {outs.map(([lbl, val, hint, kind]) => (
            <div key={lbl} className="flex justify-between py-0.5">
              <span className="text-muted-foreground flex items-center gap-1.5" title={hint}>
                <span>− {lbl}</span>
                {(kind === 'settlements' || kind === 'cogs') && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      const next = auditOpen === kind ? null : kind;
                      setAuditOpen(next);
                      if (next) loadAudit();
                    }}
                    className="text-xs text-info hover:text-info-foreground hover:underline cursor-pointer font-sans font-semibold px-1 py-0.5 rounded bg-info/10 hover:bg-info/20 transition-colors"
                    data-testid={`${mode}-${kind === 'cogs' ? 'cogs' : 'settlement'}-audit-${branchId}`}
                    title={kind === 'cogs'
                      ? 'Click to see which supplier-purchase expenses make up this number'
                      : 'Click to see which supplier payments make up this number'}
                  >
                    {auditOpen === kind ? 'hide ↑' : 'audit ↗'}
                  </button>
                )}
              </span>
              <span className="text-error">−{(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>
          ))}

          {/* iter395e — Stock Purchases audit sub-panel */}
          {auditOpen === 'cogs' && (
            <div className="mt-1.5 ml-2 mr-1 mb-1 rounded-lg border border-amber-300/50 bg-amber-50/40 dark:bg-amber-950/20 p-2 font-sans" data-testid={`${mode}-cogs-audit-panel-${branchId}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold uppercase tracking-wider text-amber-900 dark:text-amber-200">
                  Stock Purchases — {mode}
                  {filteredCogsExpenses.length > 0 && <span className="ml-2 normal-case font-normal text-muted-foreground">({filteredCogsExpenses.length} expense{filteredCogsExpenses.length === 1 ? '' : 's'})</span>}
                </span>
                <div className="flex items-center gap-2">
                  {filteredCogsExpenses.length > 0 && (
                    <button type="button" onClick={() => exportToCsv(`stock_purchases_${mode}_${slug(branchId)}.csv`, filteredCogsExpenses, [
                      { key: 'date', label: 'Date', format: (v) => (v || '').slice(0, 10) },
                      { key: 'supplier_name', label: 'Supplier' },
                      { key: 'description', label: 'Description' },
                      { key: 'payment_mode', label: 'Mode' },
                      { key: 'amount', label: 'Amount', format: (v) => Number(v || 0).toFixed(2) },
                    ])} className="text-xs text-info hover:underline font-semibold" data-testid={`${mode}-cogs-audit-export-${branchId}`}>⬇ CSV</button>
                  )}
                  <button
                    type="button"
                    onClick={() => setAuditOpen(null)}
                    className="text-xs text-muted-foreground hover:text-foreground"
                    data-testid={`${mode}-cogs-audit-close-${branchId}`}
                  >
                    ✕
                  </button>
                </div>
              </div>
              {auditLoading && <div className="text-xs text-muted-foreground py-2">Loading…</div>}
              {auditErr && (
                <div className="text-xs text-rose-700 bg-rose-50 px-2 py-1.5 rounded border border-rose-200">
                  Couldn&apos;t load detail: {auditErr}
                  <button type="button" onClick={() => { setAuditData(null); setAuditErr(null); loadAudit(); }} className="ml-2 underline">retry</button>
                </div>
              )}
              {!auditLoading && !auditErr && filteredCogsExpenses.length === 0 && (
                <div className="text-xs text-muted-foreground py-2 italic">
                  No {mode}-paid stock purchases in this period for this branch. (Credit-mode purchases roll up under &quot;Settlements of old credit bills&quot; when paid.)
                </div>
              )}
              {filteredCogsExpenses.length > 0 && (
                <div className="overflow-x-auto -mx-1 max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="text-xs uppercase text-muted-foreground bg-amber-100/60 dark:bg-amber-900/40 sticky top-0">
                      <tr>
                        <th className="text-left px-1.5 py-1">Date</th>
                        <th className="text-left px-1.5 py-1">Supplier</th>
                        <th className="text-left px-1.5 py-1">Description</th>
                        <th className="text-left px-1.5 py-1">Mode</th>
                        <th className="text-right px-1.5 py-1">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCogsExpenses.map((e, i) => (
                        <tr key={i} className="border-t border-amber-200/40 hover:bg-amber-100/30">
                          <td className="px-1.5 py-1 text-muted-foreground">{(e.date || '').slice(0, 10)}</td>
                          <td className="px-1.5 py-1 font-medium">{e.supplier_name || '-'}</td>
                          <td className="px-1.5 py-1 text-xs text-muted-foreground truncate max-w-[180px]" title={e.description || ''}>{e.description || '-'}</td>
                          <td className="px-1.5 py-1 capitalize">{e.payment_mode || '-'}</td>
                          <td className="px-1.5 py-1 text-right font-mono tabular-nums">{fmtSR(e.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-amber-300/60 font-semibold">
                        <td colSpan={4} className="px-1.5 py-1 text-right text-amber-900 dark:text-amber-200">Total ({mode})</td>
                        <td className="px-1.5 py-1 text-right font-mono tabular-nums text-amber-900 dark:text-amber-200">{fmtSR(filteredCogsExpenses.reduce((sum, e) => sum + (e.amount || 0), 0))}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
              {auditData?.truncated && (
                <div className="text-xs text-amber-700 italic mt-1">Showing first 500 expenses — narrow your date range to see all.</div>
              )}
            </div>
          )}

          {/* iter395d — Settlement audit sub-panel */}
          {auditOpen === 'settlements' && (
            <div className="mt-1.5 ml-2 mr-1 mb-1 rounded-lg border border-info/30 bg-info/5 p-2 font-sans" data-testid={`${mode}-settlement-audit-panel-${branchId}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold uppercase tracking-wider text-info">
                  Settlement detail — {mode}
                  {filteredPayments.length > 0 && <span className="ml-2 normal-case font-normal text-muted-foreground">({filteredPayments.length} payment{filteredPayments.length === 1 ? '' : 's'})</span>}
                </span>
                <div className="flex items-center gap-2">
                  {filteredPayments.length > 0 && (
                    <button type="button" onClick={() => exportToCsv(`settlements_${mode}_${slug(branchId)}.csv`, filteredPayments, [
                      { key: 'date', label: 'Date', format: (v) => (v || '').slice(0, 10) },
                      { key: 'supplier_name', label: 'Supplier' },
                      { key: 'payment_mode', label: 'Mode' },
                      { key: 'bank_payment_id', label: 'Source', format: (v) => (v ? 'Bank Payment / Quick-Link' : 'Pay Credit / Supplier Payments') },
                      { key: 'amount', label: 'Amount', format: (v) => Number(v || 0).toFixed(2) },
                    ])} className="text-xs text-info hover:underline font-semibold" data-testid={`${mode}-settlement-audit-export-${branchId}`}>⬇ CSV</button>
                  )}
                  <button
                    type="button"
                    onClick={() => setAuditOpen(null)}
                    className="text-xs text-muted-foreground hover:text-foreground"
                    data-testid={`${mode}-settlement-audit-close-${branchId}`}
                  >
                    ✕
                  </button>
                </div>
              </div>
              {auditLoading && <div className="text-xs text-muted-foreground py-2">Loading…</div>}
              {auditErr && (
                <div className="text-xs text-rose-700 bg-rose-50 px-2 py-1.5 rounded border border-rose-200">
                  Couldn&apos;t load settlement detail: {auditErr}
                  <button type="button" onClick={() => { setAuditData(null); setAuditErr(null); loadAudit(); }} className="ml-2 underline">retry</button>
                </div>
              )}
              {!auditLoading && !auditErr && filteredPayments.length === 0 && (
                <div className="text-xs text-muted-foreground py-2 italic">
                  No {mode} settlements in this period for this branch.
                </div>
              )}
              {filteredPayments.length > 0 && (
                <div className="overflow-x-auto -mx-1 max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="text-xs uppercase text-muted-foreground bg-info/10 sticky top-0">
                      <tr>
                        <th className="text-left px-1.5 py-1">Date</th>
                        <th className="text-left px-1.5 py-1">Supplier</th>
                        <th className="text-left px-1.5 py-1">Mode</th>
                        <th className="text-left px-1.5 py-1">Source</th>
                        <th className="text-right px-1.5 py-1">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPayments.map((p, i) => {
                        const src = p.bank_payment_id ? 'Bank Payment / Quick-Link' : 'Pay Credit / Supplier Payments';
                        return (
                          <tr key={i} className="border-t border-info/20 hover:bg-info/10">
                            <td className="px-1.5 py-1 text-muted-foreground">{(p.date || '').slice(0, 10)}</td>
                            <td className="px-1.5 py-1 font-medium">{p.supplier_name || '-'}</td>
                            <td className="px-1.5 py-1 capitalize">{p.payment_mode || '-'}</td>
                            <td className="px-1.5 py-1 text-xs text-muted-foreground" title={p.bank_payment_id || p.id || ''}>{src}</td>
                            <td className="px-1.5 py-1 text-right font-mono tabular-nums">{fmtSR(p.amount)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-info/40 font-semibold">
                        <td colSpan={4} className="px-1.5 py-1 text-right text-info">Total ({mode})</td>
                        <td className="px-1.5 py-1 text-right font-mono tabular-nums text-info">{fmtSR(filteredPayments.reduce((sum, p) => sum + (p.amount || 0), 0))}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
              {auditData?.truncated && (
                <div className="text-xs text-amber-700 italic mt-1">Showing first 500 settlements — narrow your date range to see all.</div>
              )}
            </div>
          )}

          <div className="flex justify-between py-0.5 border-t border-border/60 pt-1 font-semibold">
            <span>= Net {mode === 'cash' ? 'Cash' : 'Bank'} Movement</span>
            <span className={bd.net >= 0 ? 'text-success' : 'text-error'}>{(bd.net || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="pt-2 text-xs leading-relaxed text-muted-foreground italic border-t border-border/40 mt-1">
            <b>Why is this negative?</b> A negative movement doesn&apos;t mean you lost money — it means you paid out more {mode} than came in {mode} this period. Commonly because you settled old credit supplier bills. Your actual balance is the Total Liquid shown below (opening + this period).
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * CogsDrillRow (iter382) — clickable COGS row that opens a dialog listing
 * the underlying supplier expenses and supplier payments making up the
 * total. Gives users the audit trail they asked for.
 */
function CogsDrillRow({ b, startDate, endDate, onOpen }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // iter395d — Surface API errors instead of silently swallowing them.
  // Previously a failed fetch left the modal blank and users thought the
  // click did nothing. We now show a visible error message + retry CTA.
  const [err, setErr] = useState(null);
  const load = async () => {
    if (data || loading) return;
    setLoading(true);
    setErr(null);
    try {
      const params = { branch_id: b.branch_id };
      if (startDate) params.start_date = startDate;
      if (endDate)   params.end_date = endDate;
      const { data: d } = await api.get('/accounting/branch-cogs-detail', { params });
      setData(d);
    } catch (e) {
      // Show the real error to the user instead of a blank modal.
      setErr(e?.response?.data?.detail || e?.message || 'Failed to load COGS detail');
    }
    finally { setLoading(false); }
  };
  // iter922 — export the loaded COGS detail rows (supplier expenses +
  // settlement payments) to CSV via the shared handleExport utility.
  const handleCogsExport = () => {
    if (!data) return;
    const mLabel = (d) => {
      if (!d) return '';
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return '';
      return dt.toLocaleString('en-US', { month: 'short' }) + dt.getFullYear();
    };
    const sLbl = mLabel(startDate);
    const eLbl = mLabel(endDate);
    const rangeLbl = sLbl && eLbl && sLbl !== eLbl ? `${sLbl}-${eLbl}` : (sLbl || eLbl || 'AllTime');
    const brName = (b.branch_name || 'branch').replace(/[^a-zA-Z0-9]+/g, '').toUpperCase();
    const rows = [
      ...(data.expenses || []).map((e) => ({
        type: 'Expense', date: (e.date || '').slice(0, 10), supplier: e.supplier_name || '',
        category: e.category || '', description: e.description || '', mode: e.payment_mode || '', amount: e.amount,
      })),
      ...(data.payments || []).map((p) => ({
        type: 'Payment', date: (p.date || '').slice(0, 10), supplier: p.supplier_name || '',
        category: 'supplier_payment', description: p.invoice_number || '', mode: p.payment_mode || '', amount: p.amount,
      })),
    ];
    handleExport(rows, `cost_of_goods_${brName}_${rangeLbl}.csv`, [
      { key: 'type', label: 'Type' },
      { key: 'date', label: 'Date' },
      { key: 'supplier', label: 'Supplier' },
      { key: 'category', label: 'Category' },
      { key: 'description', label: 'Description' },
      { key: 'mode', label: 'Mode' },
      { key: 'amount', label: 'Amount' },
    ], `Cost of Goods — ${b.branch_name}`);
  };
  return (
    <>
      <div className="flex justify-between items-center px-4 py-2 border-t border-border bg-warning/10 hover:bg-warning/20 transition">
        <button
          type="button"
          onClick={() => (onOpen ? onOpen() : navigate(`/bills?branch=${encodeURIComponent(b.branch_id)}`))}
          className="text-left flex-1 min-w-0 cursor-pointer"
          data-testid={`cogs-drill-${b.branch_id}`}
          title="Show the purchase transactions behind this amount"
        >
          <span className="font-semibold text-warning flex items-center gap-1">
            Cost of Goods
            <span className="text-xs font-normal opacity-70">(click to see detail →)</span>
          </span>
        </button>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => { setOpen(true); load(); }}
            className="text-xs font-medium text-warning/80 underline underline-offset-2 hover:text-warning"
            data-testid={`cogs-audit-${b.branch_id}`}
            title="View supplier expense/payment audit breakdown"
          >
            audit
          </button>
          <span className="font-mono font-bold text-warning">({fmtSR(b.cogs)})</span>
        </div>
      </div>
      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="cogs-dialog">
          <div className="bg-white dark:bg-stone-900 rounded-2xl w-full max-w-3xl max-h-[85dvh] overflow-hidden shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <div>
                <div className="font-bold">Cost of Goods — {b.branch_name}</div>
                <div className="text-xs text-muted-foreground">
                  {loading ? 'Loading…' : data ? `${data.totals.expenses_count} supplier expenses · ${data.totals.payments_count} payments · ${data.by_supplier.length} suppliers` : ''}
                </div>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground" data-testid="cogs-dialog-close">✕</button>
            </div>
            <div className="px-5 py-2.5 border-b border-border bg-warning/5 flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">Cost of Goods is driven by this branch&apos;s supplier bills.</span>
              <button
                type="button"
                onClick={() => navigate(`/bills?branch=${encodeURIComponent(b.branch_id)}`)}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-warning text-white hover:bg-warning/90 transition inline-flex items-center gap-1 shrink-0"
                data-testid={`cogs-open-bills-${b.branch_id}`}
              >
                <FileText size={13} /> View Bills for this Branch →
              </button>
            </div>
            <div className="overflow-y-auto p-4 space-y-4">
              {data && (
                <>
                  {data.totals.expenses_count === 0 && data.totals.payments_count === 0 && (
                    <div className="text-center text-muted-foreground py-8 bg-amber-50/40 rounded-xl border border-amber-100">
                      No supplier expenses or payments found for this branch in the selected period.
                      <div className="text-xs mt-2">Try widening the date range at the top of the page.</div>
                    </div>
                  )}                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    <div className="rounded-xl bg-warning/10 p-3">
                      <div className="text-xs uppercase text-muted-foreground">Expense Total</div>
                      <div className="font-bold text-warning">{fmtSR(data.totals.expense_total)}</div>
                    </div>
                    <div className="rounded-xl bg-info/10 p-3">
                      <div className="text-xs uppercase text-muted-foreground">Paid to Suppliers</div>
                      <div className="font-bold text-info">{fmtSR(data.totals.payment_total)}</div>
                    </div>
                    <div className={`rounded-xl ${data.totals.net_owed > 0 ? 'bg-error/10' : 'bg-success/10'} p-3`}>
                      <div className="text-xs uppercase text-muted-foreground">Net Owed to Suppliers</div>
                      <div className={`font-bold ${data.totals.net_owed > 0 ? 'text-error' : 'text-success'}`}>{fmtSR(data.totals.net_owed)}</div>
                    </div>
                  </div>

                  <div>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">By Supplier</div>
                    <div className="rounded-xl border border-border overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/40 text-xs uppercase">
                            <tr>
                              <th className="text-left p-2">Supplier</th>
                              <th className="text-right p-2">Expense</th>
                              <th className="text-right p-2">Paid</th>
                              <th className="text-right p-2">Net Owed</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.by_supplier.map(s => <CogsSupplierRow key={s.supplier_id} s={s} />)}
                            {data.by_supplier.length === 0 && <CogsEmptyRow colSpan={4} label="No supplier data in this period." />}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {data.expenses.length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Supplier Expenses ({data.expenses.length}){data.truncated && ' — showing first 500'}</div>
                      <div className="rounded-xl border border-border overflow-hidden max-h-64 overflow-y-auto">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-muted/40 text-xs uppercase sticky top-0">
                              <tr>
                                <th className="text-left p-2">Date</th>
                                <th className="text-left p-2">Category</th>
                                <th className="text-left p-2">Description</th>
                                <th className="text-right p-2">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {data.expenses.map((e, i) => <CogsExpenseRow key={i} e={e} />)}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                  {data.payments && data.payments.length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-wide text-info mb-1">💵 Supplier Payments (settlements of old credit) — {data.payments.length}</div>
                      <div className="rounded-xl border border-info/30 overflow-hidden max-h-64 overflow-y-auto bg-info/5">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-info/10 text-xs uppercase sticky top-0">
                              <tr>
                                <th className="text-left p-2">Date</th>
                                <th className="text-left p-2">Supplier</th>
                                <th className="text-left p-2">Invoice / Ref</th>
                                <th className="text-left p-2">Mode</th>
                                <th className="text-right p-2">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {data.payments.map((p, i) => (
                                <tr key={i} className="border-t border-info/20">
                                  <td className="p-2 text-xs">{(p.date || '').slice(0, 10)}</td>
                                  <td className="p-2">{p.supplier_name || '-'}</td>
                                  <td className="p-2 text-xs text-muted-foreground">{p.invoice_number || '-'}</td>
                                  <td className="p-2 text-xs capitalize">{p.payment_mode || '-'}</td>
                                  <td className="p-2 text-right font-mono">{fmtSR(p.amount)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
              {loading && <div className="text-center text-muted-foreground py-8">Loading detail…</div>}
              {err && (
                <div className="bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/50 rounded-xl p-4 text-sm text-rose-800 dark:text-rose-200" data-testid="cogs-dialog-error">
                  <div className="font-semibold mb-1">Couldn&apos;t load supplier detail</div>
                  <div className="text-xs leading-relaxed">{err}</div>
                  <button
                    type="button"
                    onClick={() => { setData(null); setErr(null); load(); }}
                    className="mt-3 text-xs px-2.5 py-1 rounded-md bg-rose-100 dark:bg-rose-900/50 hover:bg-rose-200 dark:hover:bg-rose-900/70 font-semibold"
                    data-testid="cogs-dialog-retry"
                  >
                    Retry
                  </button>
                </div>
              )}
            </div>
            <div className="px-5 py-2.5 border-t border-border flex justify-end gap-2 items-center">
              <button
                type="button"
                onClick={handleCogsExport}
                disabled={!data}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 transition inline-flex items-center gap-1"
                data-testid={`cogs-check-export-${b.branch_id}`}
                title="Validate the COGS detail and export it as an Excel-ready CSV"
              >
                <FileText size={13} /> Check &amp; Export
              </button>
              <Link to={`/expenses?branch=${b.branch_id}&start_date=${startDate || ''}&end_date=${endDate || ''}`} className="text-xs text-info hover:underline" data-testid="cogs-open-expenses">Open in Expenses →</Link>
              <Link to={`/suppliers`} className="text-xs text-info hover:underline" data-testid="cogs-open-suppliers">Open Suppliers →</Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Bills Due Soon — surfaces unpaid credit invoices and overdue/upcoming
 * recurring expenses for a single branch. Helps the user plan cash
 * outflow so a "Cash in Hand" deficit doesn't surprise them.
 */
function BillsDueSoon({ branchId, branchName, asOf }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [allRows, setAllRows] = useState(null);
  const [allErr, setAllErr] = useState(null);

  useEffect(() => {
    if (!branchId || branchId === '__unassigned__') return;
    setData(null); setAllRows(null); setAllErr(null);
    const params = { branch_id: branchId, horizon_days: 30, as_of: asOf || undefined };
    api.get('/accounting/bills-due-soon', { params: { ...params, page_size: 100 } }).then(async (r) => {
      setData(r.data);
      const t = r.data?.totals || {};
      const acc = { overdue: [], due_soon: [] };
      for (let page = 1; page <= 50; page += 1) { // server-paginated: load every row for exports
        const c = (await api.get('/accounting/bills-due-soon', { params: { ...params, page, page_size: 500 } })).data || {};
        acc.overdue.push(...(c.overdue || [])); acc.due_soon.push(...(c.due_soon || []));
        if (acc.overdue.length >= (t.overdue_count || 0) && acc.due_soon.length >= (t.due_soon_count || 0)) break;
        if ((c.overdue || []).length < 500 && (c.due_soon || []).length < 500) break;
      }
      setAllRows(acc);
    }).catch((e) => setAllErr(`Could not load bills: ${e?.response?.data?.detail || e.message}`));
  }, [branchId, asOf]);

  if (!data) return null;
  const { totals, overdue = [], due_soon = [] } = data;
  if (!totals?.overdue_count && !totals?.due_soon_count) return null; // nothing to show

  const billCols = [
    { key: 'supplier', label: 'Supplier' }, { key: 'invoice_number', label: 'Bill no.' }, { key: 'bill_date', label: 'Bill date' },
    { key: 'due_date', label: 'Due date' }, { key: 'description', label: 'Description' },
    { key: 'amount', label: 'Bill amount', numeric: true, format: (v) => Number(v || 0).toFixed(2) },
    { key: 'paid', label: 'Paid', numeric: true, format: (v) => Number(v || 0).toFixed(2) },
    { key: 'outstanding', label: 'Remaining', numeric: true, format: (v) => Number(v || 0).toFixed(2) },
    { key: 'basis', label: 'Basis', format: (v, r) => (r.kind === 'recurring' ? 'Recurring' : v === 'linked' ? 'Linked payments' : v === 'fifo_estimate' ? 'FIFO estimate' : 'No payment') },
    { key: 'duplicate_suspect', label: 'Suspected duplicate', format: (v) => v || '' },
  ];
  const exportBills = (key, format) => {
    // Rows are preloaded so the download runs inside the click (mobile browsers block downloads after an await).
    if (!allRows) { toast.error(allErr || 'Bills are still loading — try again in a moment'); return; }
    const rows = allRows[key];
    const count = key === 'overdue' ? totals.overdue_count : totals.due_soon_count;
    const sum = Math.round(rows.reduce((a, x) => a + Number(x.outstanding || 0), 0) * 100) / 100;
    const want = key === 'overdue' ? totals.overdue : totals.due_soon;
    if (Math.abs(sum - want) >= 0.01 || rows.length !== count) toast.warning(`Export ${rows.length} bills / ${fmtSR(sum)} differs from summary ${count} / ${fmtSR(want)}`);
    else toast.success(`${rows.length} bills · ${fmtSR(sum)} — matches the summary`);
    const basis = data.historical ? `As-of-date outstanding ${data.as_of} (payments after this date excluded)` : `Current outstanding (today ${data.as_of})`;
    const cur = data.current ? [{ supplier: `Current Outstanding today ${data.current.as_of} (all bills, not part of this report)`, outstanding: data.current.outstanding }] : [];
    try {
      exportRows(format, { title: `${key === 'overdue' ? 'Overdue' : 'Due in 30 days'} bills — ${branchName || ''}`, subtitle: `${basis} · ${data.basis_note || ''}`,
        filename: `${key}_bills_${slug(branchName || branchId)}_as_of_${data.as_of}`, cols: billCols,
        rows: [...rows, { supplier: `TOTAL (${basis})`, outstanding: sum }, ...cur] });
    } catch (e) { toast.error(`Export failed: ${e.message || e}`); }
  };
  const trio = (key) => (
    <span className="inline-flex gap-1" data-testid={`bills-export-${key}-${branchId}`}>
      {['xlsx', 'pdf', 'csv'].map((f) => (
        <button key={f} type="button" onClick={() => exportBills(key, f)} className="rounded-full border border-border px-1.5 py-0.5 text-[10px] hover:bg-muted" data-testid={`bills-export-${key}-${f}-${branchId}`}>{f === 'xlsx' ? 'Excel' : f.toUpperCase()}</button>
      ))}
    </span>
  );

  return (
    <>
      <div className="px-4 py-1.5 bg-amber-100/70 dark:bg-amber-950/30 border-t border-amber-200 dark:border-amber-900/60 flex items-center justify-between" data-testid={`bills-due-header-${branchId}`}>
        <span className="text-xs uppercase tracking-wider text-amber-900 dark:text-amber-200 font-semibold">📅 Bills Due Soon <span className="normal-case tracking-normal font-normal" data-testid={`bills-as-of-${branchId}`}>· {data.historical ? `outstanding as of ${data.as_of}` : `current outstanding (today ${data.as_of})`}</span></span>
        <button type="button" onClick={() => setOpen((o) => !o)} className="text-xs text-amber-700 dark:text-amber-300 hover:underline" data-testid={`bills-due-toggle-${branchId}`}>
          {open ? 'Hide' : 'Show'}
        </button>
      </div>
      <Row
        label={
          <span className="flex items-center gap-2">
            Overdue
            {totals.overdue_count > 0 && (
              <span className="text-xs bg-rose-200 text-rose-900 dark:bg-rose-900/50 dark:text-rose-200 px-1.5 py-0.5 rounded">
                {totals.overdue_count} bill{totals.overdue_count === 1 ? '' : 's'}
              </span>
            )}
          </span>
        }
        value={fmtSR(totals.overdue)}
        indent
        color={totals.overdue > 0 ? 'text-rose-700' : 'text-muted-foreground'}
        bg="bg-rose-50/40"
        extra={totals.overdue_count > 0 ? trio('overdue') : undefined}
      />
      <Row
        label={
          <span className="flex items-center gap-2">
            Due in next 30 days
            {totals.due_soon_count > 0 && (
              <span className="text-xs bg-amber-200 text-amber-900 dark:bg-amber-900/50 dark:text-amber-200 px-1.5 py-0.5 rounded">
                {totals.due_soon_count} bill{totals.due_soon_count === 1 ? '' : 's'}
              </span>
            )}
          </span>
        }
        value={fmtSR(totals.due_soon)}
        indent
        color={totals.due_soon > 0 ? 'text-amber-800' : 'text-muted-foreground'}
        bg="bg-amber-50/30"
        extra={totals.due_soon_count > 0 ? trio('due_soon') : undefined}
      />
      {data.current && (
        <Row label={<span>Current Outstanding <span className="text-[10px] text-muted-foreground">(today {data.current.as_of} · {data.current.count} bills · includes payments/bills after {data.as_of})</span></span>}
          value={fmtSR(data.current.outstanding)} indent color="text-muted-foreground" />
      )}
      {open && (
        <div className="px-4 py-2 bg-amber-50/20 dark:bg-amber-950/10 border-t border-amber-100 dark:border-amber-900/40">
          {overdue.length > 0 && (
            <div className="mb-2">
              <div className="text-xs font-bold uppercase tracking-wider text-rose-800 mb-1">Overdue</div>
              {overdue.slice(0, 8).map((b, i) => (
                <div key={`o-${b.id || i}`} className="flex items-center justify-between text-xs py-1 border-b border-rose-100 dark:border-rose-900/30 last:border-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-rose-600">{b.kind === 'recurring' ? '🔁' : '📝'}</span>
                    <span className="truncate font-medium" title={b.description}>{b.supplier || b.description}</span>
                    <span className="text-muted-foreground truncate text-[10px]">{b.invoice_number ? `#${b.invoice_number} · ` : ''}{b.bill_date ? `billed ${b.bill_date}` : ''}{b.basis === 'fifo_estimate' ? ' · FIFO est.' : ''}{b.duplicate_suspect ? <b className="text-rose-700"> · possible duplicate ({b.duplicate_suspect})</b> : null}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[10px] text-muted-foreground font-mono">{b.paid ? `of ${fmtSR(b.amount)}` : ''}</span>
                    <span className="text-rose-700 font-mono">{fmtSR(b.outstanding)}</span>
                    <span className="text-xs text-rose-600">{b.due_date || '—'}</span>
                  </div>
                </div>
              ))}
              {totals.overdue_count > 8 && <div className="text-xs text-muted-foreground mt-1">+ {totals.overdue_count - 8} more overdue — use Excel/PDF export for the full list</div>}
              <div className="text-[10px] text-muted-foreground">{data.basis_note}</div>
              {totals.duplicate_suspect_count > 0 && <div className="text-[10px] text-rose-700" data-testid={`bills-dup-${branchId}`}>{totals.duplicate_suspect_count} suspected duplicate bills ({fmtSR(totals.duplicate_suspect_amount)}) — flagged only, review before paying</div>}
            </div>
          )}
          {due_soon.length > 0 && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-amber-800 mb-1">Due in next 30 days</div>
              {due_soon.slice(0, 8).map((b, i) => (
                <div key={`d-${b.id || i}`} className="flex items-center justify-between text-xs py-1 border-b border-amber-100 dark:border-amber-900/30 last:border-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-amber-600">{b.kind === 'recurring' ? '🔁' : '📝'}</span>
                    <span className="truncate font-medium" title={b.description}>{b.description}</span>
                    {b.supplier ? <span className="text-muted-foreground truncate">· {b.supplier}</span> : null}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-amber-800 font-mono">{fmtSR(b.outstanding)}</span>
                    <span className="text-xs text-amber-700">{b.due_date}</span>
                  </div>
                </div>
              ))}
              {totals.due_soon_count > 8 && <div className="text-xs text-muted-foreground mt-1">+ {totals.due_soon_count - 8} more upcoming — use export for the full list</div>}
            </div>
          )}
        </div>
      )}
    </>
  );
}


/**
 * Cash Flow Statement — Opening + Period Movement = Closing balance.
 * This is what answers "what's actually in the till right now?" — the
 * `cash_in_hand` shown above is just net movement for the selected period
 * which can be misleadingly negative when the user paid off old credit.
 */
function CashFlowStatement({ branchId, startDate, endDate, netIncome }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancel = false;
    if (!branchId || branchId === '__unassigned__' || !startDate || !endDate) return;
    (async () => {
      try {
        const r = await api.get('/accounting/cash-flow-statement', {
          params: { branch_id: branchId, start_date: startDate, end_date: endDate, include_net_income: false },
        });
        if (!cancel) setData({ ...r.data, net_income: Math.round(Number(netIncome || 0) * 100) / 100 });
      } catch {
        // silent
      }
    })();
    return () => { cancel = true; };
  }, [branchId, startDate, endDate, netIncome]);

  if (!data) return null;
  const { opening, period_movement: pm, closing } = data;

  const Line = ({ label, value, indent = 0, bold, color }) => (
    <div className={`flex justify-between items-center py-1.5 text-xs ${indent ? `pl-${indent * 3}` : ''}`}>
      <span className={`${bold ? 'font-semibold text-foreground' : 'text-muted-foreground'} ${color || ''}`}>{label}</span>
      <span className={`font-mono tabular-nums ${bold ? 'font-bold' : ''} ${value < 0 ? 'text-rose-700' : value > 0 ? 'text-emerald-700' : 'text-muted-foreground'}`}>
        {fmtSR(value)}
      </span>
    </div>
  );

  return (
    <>
      <div className="px-4 py-1.5 bg-emerald-100/70 dark:bg-emerald-950/30 border-t border-emerald-200 dark:border-emerald-900/60 flex items-center justify-between" data-testid={`cashflow-header-${branchId}`}>
        <span className="text-xs uppercase tracking-wider text-emerald-900 dark:text-emerald-200 font-semibold">📊 What's in the Till — Closing Balance</span>
        <button type="button" onClick={() => setOpen((o) => !o)} className="text-xs text-emerald-700 dark:text-emerald-300 hover:underline" data-testid={`cashflow-toggle-${branchId}`}>
          {open ? 'Hide breakdown' : 'Show breakdown'}
        </button>
      </div>
      <div className="px-4 py-2 bg-blue-50/60 dark:bg-blue-950/20 border-t border-blue-100 dark:border-blue-900/40 flex items-center justify-between" data-testid={`cashflow-net-income-${branchId}`}>
        <span className="text-xs uppercase tracking-wider text-blue-900 dark:text-blue-200 font-semibold">Net Income (matches Branch Summary Net)</span>
        <span className={`font-mono font-bold text-sm tabular-nums ${(data.net_income ?? 0) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{fmtSR(data.net_income ?? 0)}</span>
      </div>
      <div className="px-4 py-2.5 bg-emerald-50/50 dark:bg-emerald-950/15 border-t border-emerald-100 dark:border-emerald-900/40 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        <div className="text-center">
          <div className="text-xs uppercase text-muted-foreground tracking-wider">Cash</div>
          <div className={`font-mono font-bold text-sm tabular-nums ${closing.cash >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{fmtSR(closing.cash)}</div>
        </div>
        <div className="text-center border-l border-r border-emerald-100 dark:border-emerald-900/40">
          <div className="text-xs uppercase text-muted-foreground tracking-wider">Bank</div>
          <div className={`font-mono font-bold text-sm tabular-nums ${closing.bank >= 0 ? 'text-info' : 'text-rose-700'}`}>{fmtSR(closing.bank)}</div>
        </div>
        <div className="text-center">
          <div className="text-xs uppercase text-muted-foreground tracking-wider">Total Liquid</div>
          <div className={`font-mono font-bold text-sm tabular-nums ${closing.total >= 0 ? 'text-success' : 'text-rose-700'}`}>{fmtSR(closing.total)}</div>
        </div>
      </div>
      {open && (
        <div className="px-4 py-2 bg-emerald-50/20 dark:bg-emerald-950/10 border-t border-emerald-100 dark:border-emerald-900/40 space-y-2">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Opening (as of {opening.as_of})</div>
          <div className="pl-2">
            <Line label="Cash" value={opening.cash} />
            <Line label="Bank" value={opening.bank} />
            <Line label="Total Opening" value={opening.total} bold />
          </div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold pt-1">+ Movement This Period</div>
          <div className="pl-2">
            <Line label="Cash inflow" value={pm.cash.in} />
            <Line label="Cash outflow" value={-pm.cash.out} />
            <Line label="Net cash" value={pm.cash.net} bold />
            <Line label="Bank inflow" value={pm.bank.in} />
            <Line label="Bank outflow" value={-pm.bank.out} />
            <Line label="Net bank" value={pm.bank.net} bold />
            <Line label="Net movement" value={pm.total_net} bold />
          </div>
          <div className="text-xs uppercase tracking-wider text-emerald-800 dark:text-emerald-200 font-semibold pt-1 border-t border-emerald-200 dark:border-emerald-900/40 pt-2">= Closing Balance</div>
          <div className="pl-2">
            <Line label="Cash in Hand" value={closing.cash} bold />
            <Line label="Bank in Hand" value={closing.bank} bold />
            <Line label="Total Liquid" value={closing.total} bold color="text-emerald-700" />
          </div>
        </div>
      )}
    </>
  );
}


